import { eligibility } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * The eligibility screens' decisions, out of the components so they can be
 * asserted without mounting anything.
 *
 * The three things this file exists to keep straight — each of them a state the
 * screens were at risk of collapsing into another:
 *
 *   WITHHELD vs ABSENT — a hold read without `directory:manage` has NO reason
 *     fields at all, and one read WITH it can still have a null note. Those are
 *     different sentences and {@link reasonReading} is the only place that
 *     decides which.
 *   RESTORABLE: yes / no / NOT ASKED — `restorableWithoutManagement` is null for
 *     an eligible worker, and rendering null as "no" answers a question about
 *     somebody who is not restricted. {@link restorationReading}.
 *   PLACED BY THE PLATFORM vs BY A PERSON — `placedBySystem` is the difference
 *     between a lapse the sweep noticed and a decision somebody made, and it
 *     changes who to go and talk to.
 */

// ---------------------------------------------------------------- vocabulary

/**
 * How each reason category reads, and it is a `Record` over the full union
 * rather than a lookup with a fallback.
 *
 * A fallback would silently label a category the server grew as whatever the
 * default said — the exact defect `SESSION_STATUS_READING` was written to end,
 * where three supervision statuses printed as "Voided". A category added
 * upstream must be a compile error here.
 */
export const REASON_CATEGORY_READING: Record<eligibility.ReasonCategory, string> = {
  credential_requirement: 'Credential requirement not met',
  supervision_requirement: 'Supervision requirement not met',
  onboarding_requirement: 'Onboarding requirement not met',
  missing_program_prerequisite: 'Missing program prerequisite',
  administrative_hold: 'Administrative hold',
  investigation: 'Investigation',
  performance_hold: 'Performance hold',
  hr_management: 'HR / management decision',
  other: 'Other',
};

export const SCOPE_READING: Record<eligibility.EligibilityScopeType, string> = {
  // "Every kind of work" and not "the organization": the scope is how far ONE
  // person's hold reaches, and "organization-wide hold" reads on a compliance
  // screen as though the organization itself were restricted.
  organization: 'All work',
  program: 'One program',
  role: 'One role',
};

export const RESTORATION_READING: Record<eligibility.RestorationMode, string> = {
  automatic_when_requirement_verified: 'Clears itself when the requirement is met',
  management_only: 'Only a person can lift it',
};

/**
 * The history's event kinds. `restoration_refused` is the one worth spelling
 * out: it is the sweep having tried to lift a hold and been refused, which is a
 * different fact from the hold simply still standing.
 */
export const EVENT_READING: Record<eligibility.EligibilityEventType, string> = {
  placed: 'Hold placed',
  lifted: 'Hold lifted',
  auto_lifted: 'Cleared automatically',
  restoration_refused: 'Automatic restore refused',
  reason_amended: 'Reason amended',
};

// -------------------------------------------------------------- withholding

/**
 * Withheld, absent, or here — for a hold's management reason.
 *
 * `kind` rather than a string, so a caller must branch and cannot print one of
 * these where it meant another:
 *
 *   'withheld' — the caller lacks `directory:manage`; the reason EXISTS and the
 *                server kept it back. Never "no reason given".
 *   'absent'   — the caller may see reasons and this hold has no free-text note
 *                beside its category. A real, measured nothing.
 *   'present'  — the note, verbatim.
 *
 * The category is separately available on 'absent' and 'present' and never on
 * 'withheld', because the server moves all four reason fields as one group.
 */
export type ReasonReading =
  | { kind: 'withheld' }
  | { kind: 'absent'; category: eligibility.ReasonCategory; label: string }
  | { kind: 'present'; category: eligibility.ReasonCategory; label: string; note: string };

export function reasonReading(hold: eligibility.HoldView): ReasonReading {
  // The FLAG first, always. `reasonCategory === undefined` would usually agree
  // with it, but the flag is the field the server promises never to omit, and a
  // projection bug that dropped the category would otherwise read as a hold
  // with no category — which no hold has.
  if (hold.reasonWithheld) return { kind: 'withheld' };
  const category = hold.reasonCategory;
  if (category === undefined) {
    // Not reachable through the documented contract: `reasonWithheld: false`
    // means the group is present. Treated as withheld rather than as absent,
    // because guessing toward disclosure is the direction that does harm.
    return { kind: 'withheld' };
  }
  const label = hold.reasonLabel ?? REASON_CATEGORY_READING[category];
  const note = hold.managementReason;
  return note === null || note === undefined || note.trim() === ''
    ? { kind: 'absent', category, label }
    : { kind: 'present', category, label, note: note.trim() };
}

/** One line for a table cell. Long notes belong on the detail screen. */
export function reasonCellText(reading: ReasonReading): string {
  switch (reading.kind) {
    case 'withheld':
      return 'Withheld';
    case 'absent':
    case 'present':
      return reading.label;
  }
}

export const REASON_WITHHELD_EXPLANATION =
  'The reason for a hold is employment information about a named colleague, so it travels with the permission to manage the workforce. This is withheld, not missing.';

// ------------------------------------------------------------------ readings

/**
 * How a hold's restoration mode reads as a pill.
 *
 * 'look' rather than 'urgent' for the management-only case: it is not an
 * emergency, it is a hold that will not clear on its own and therefore needs
 * somebody's attention eventually.
 */
export function restorationPill(hold: eligibility.HoldView): {
  variant: Severity;
  label: string;
} {
  return hold.restorationMode === 'automatic_when_requirement_verified'
    ? { variant: 'sky', label: 'Clears itself' }
    : { variant: 'look', label: 'Needs a person' };
}

export function statusPill(hold: eligibility.HoldView): { variant: Severity; label: string } {
  return hold.status === 'active'
    ? { variant: 'urgent', label: 'Active' }
    : { variant: 'plain', label: 'Lifted' };
}

/**
 * Who placed it. Not decoration: a hold the sweep placed is answered by fixing
 * the requirement, and a hold a person placed is answered by talking to that
 * person.
 */
export function placedByReading(hold: eligibility.HoldView): string {
  return hold.placedBySystem ? 'Phibian (requirement sweep)' : 'A person';
}

/**
 * "Would this clear itself?" — three answers, and the third is not "no".
 *
 * `null` arrives for an ELIGIBLE worker, where the question was never asked.
 * A screen that renders it as "needs management" invents a restriction.
 */
export type RestorationReading =
  { kind: 'not-restricted' } | { kind: 'automatic' } | { kind: 'needs-management' };

export function restorationReading(value: boolean | null): RestorationReading {
  if (value === null) return { kind: 'not-restricted' };
  return value ? { kind: 'automatic' } : { kind: 'needs-management' };
}

export function restorationSentence(reading: RestorationReading): string {
  switch (reading.kind) {
    case 'not-restricted':
      return 'This worker is not restricted, so there is nothing to restore.';
    case 'automatic':
      return 'Every hold in force here clears itself once its requirement is verified. Nobody has to lift it by hand.';
    case 'needs-management':
      return 'At least one hold in force here can only be lifted by a person. It will not clear on its own, whatever else is resolved.';
  }
}

/**
 * What one impact row IS, in the reader's words.
 *
 * `kind` arrives as the server's row vocabulary. `scheduled_service` is new
 * here (PH-ARC-001: a hold's impact now lists the worker's VISITS as well as
 * their shifts, which for an EHR-sourced tenant is all of their planned work),
 * and rendering the raw column name would have put "scheduled_service" in a
 * table headed "What". Unknown kinds fall through verbatim rather than being
 * hidden: a row the server thought worth listing is not dropped by a label map.
 */
export function impactKindLabel(kind: string): string {
  switch (kind) {
    case 'scheduled_shift':
      return 'Shift';
    case 'scheduled_service':
      return 'Visit';
    case 'client_assignment':
      return 'Client assignment';
    default:
      return kind;
  }
}

/**
 * The impact list's own sentence, and the reason there is no button beside it.
 *
 * The domain SURFACES and FORECASTS impact; it does not cancel. What happens to
 * a booked shift is a judgement about a client, and a screen offering to unbook
 * them in one click would be making that judgement on management's behalf and
 * telling nobody it had.
 */
export const IMPACT_EXPLANATION =
  'These are already booked and Phibian has not touched them. A hold does not cancel work — deciding what happens to a client’s scheduled visit is yours.';

// -------------------------------------------------------------------- format

export function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDay(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** The server's sentence when it wrote one, never a status code. */
export function messageOf(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') return message.trim();
  }
  return 'That did not save. Try again.';
}

// ---------------------------------------------------------------- hold draft

export interface HoldDraft {
  workerId: string;
  scopeType: eligibility.EligibilityScopeType;
  scopeProgramId: string;
  scopeRoleKey: string;
  reasonCategory: eligibility.ReasonCategory;
  restorationMode: eligibility.RestorationMode;
  managementReason: string;
}

export const EMPTY_HOLD_DRAFT: HoldDraft = {
  workerId: '',
  scopeType: 'organization',
  scopeProgramId: '',
  scopeRoleKey: '',
  reasonCategory: 'administrative_hold',
  restorationMode: 'management_only',
  managementReason: '',
};

/**
 * What the form refuses before the server has to.
 *
 * The restoration rule is the one that matters: `eligibility_hold_restoration_ck`
 * refuses a management-shaped category with automatic restoration, and without
 * this check the operator meets a 23514 they cannot decode. The check is
 * derived from `eligibility.mayRestoreAutomatically` — the client's copy of the
 * same list — rather than retyped, so the two cannot disagree.
 */
export function validateHoldDraft(draft: HoldDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  if (draft.workerId.trim() === '') errors.workerId = 'Choose who this hold is for.';
  if (draft.scopeType === 'program' && draft.scopeProgramId.trim() === '') {
    errors.scopeProgramId = 'A program-scoped hold has to name the program.';
  }
  if (draft.scopeType === 'role' && draft.scopeRoleKey.trim() === '') {
    errors.scopeRoleKey = 'A role-scoped hold has to name the role.';
  }
  if (
    draft.restorationMode === 'automatic_when_requirement_verified' &&
    !eligibility.mayRestoreAutomatically(draft.reasonCategory)
  ) {
    errors.restorationMode =
      'This reason is a decision a person made, so only a person can lift it. Automatic restore is for requirements Phibian can verify on its own.';
  }
  if (draft.managementReason.length > 2000) {
    errors.managementReason = 'Keep the note under 2000 characters.';
  }
  return errors;
}

export function holdPayload(draft: HoldDraft): eligibility.PlaceHoldInput {
  const note = draft.managementReason.trim();
  return {
    workerId: draft.workerId,
    scopeType: draft.scopeType,
    // Sent as null rather than omitted for the scopes they do not apply to, so
    // a re-scoped draft cannot carry a stale program id into an org-wide hold.
    scopeProgramId: draft.scopeType === 'program' ? draft.scopeProgramId : null,
    scopeRoleKey: draft.scopeType === 'role' ? draft.scopeRoleKey : null,
    reasonCategory: draft.reasonCategory,
    restorationMode: draft.restorationMode,
    managementReason: note === '' ? null : note,
  };
}

/**
 * The counts on the summary card, over EXACTLY the rows that arrived.
 *
 * There is no total-workforce denominator here on purpose. The list is scoped
 * by the server (`HoldList.scope`), so "3 holds" is a claim about the caller's
 * reach, and dividing it by a workforce figure read from a different, differently
 * scoped endpoint would produce a percentage that is true of no population.
 */
export interface HoldSummary {
  total: number;
  needsAPerson: number;
  clearsItself: number;
  placedBySystem: number;
}

export function summarize(holds: readonly eligibility.HoldListRow[]): HoldSummary {
  let needsAPerson = 0;
  let clearsItself = 0;
  let placedBySystem = 0;
  for (const hold of holds) {
    if (hold.restorationMode === 'management_only') needsAPerson += 1;
    else clearsItself += 1;
    if (hold.placedBySystem) placedBySystem += 1;
  }
  return { total: holds.length, needsAPerson, clearsItself, placedBySystem };
}

/**
 * The policy card's sentence for a switch that is OFF.
 *
 * Worth writing out because an off switch is a policy choice that makes the
 * holds list legitimately shorter, and a reader who does not know it is off
 * will read the short list as a compliant workforce.
 */
export function policyOffWarning(policy: {
  autoHoldOnCredentialLapse: boolean;
  autoHoldOnSupervisionOverdue: boolean;
}): string | null {
  const off: string[] = [];
  if (!policy.autoHoldOnCredentialLapse) off.push('a lapsed credential');
  if (!policy.autoHoldOnSupervisionOverdue) off.push('overdue supervision');
  if (off.length === 0) return null;
  return `${off.join(' and ')} ${off.length === 1 ? 'does' : 'do'} not place a hold in this organization. Nobody is restricted for ${off.length === 1 ? 'it' : 'them'}, so this list being short is a policy choice rather than a clean bill of health.`;
}
